import { useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Loader2, Grid, List } from "lucide-react";
import { ApiService } from "../../services/api";
import { BaseResponse } from "../../types/base";

interface DynamicListProps {
  type?: "list" | "grid";
  header?: boolean;
  title: string;
  actions?: React.ReactNode;
  isSearch?: boolean;
  isPagination?: boolean;
  path: string;
  fetcher?: (page: number, searchTerm?: string) => Promise<BaseResponse | any>;
  refreshKey?: string | number;
  errorMessage?: string;
  emptyMessage?: string;
  item: (data: any, viewMode?: string) => React.ReactNode;
  leftArea?: () => React.ReactNode;
  showToggle?: boolean;
  dataProcessor?: (data: any[], searchTerm?: string) => any[];
  clientPageSize?: number;
  clientPaginateByRootLevelZero?: boolean;
  externalSearchTerm?: string;
  externalCurrentPage?: number;
  onSearchChange?: (term: string) => void;
  onPageChange?: (page: number) => void;
  externalViewMode?: "list" | "grid";
  onViewModeChange?: (mode: "list" | "grid") => void;
  gridClassName?: string;
  /** Planlama vb. beyaz kart içinde: dış gri panel/padding olmadan */
  embedded?: boolean;
}

export default function DynamicList({
  type = "list",
  header = true,
  title,
  actions,
  isSearch = false,
  isPagination = true,
  path,
  fetcher,
  refreshKey,
  errorMessage = "Veriler yüklenirken bir hata oluştu",
  emptyMessage = "Henüz veri bulunamadı.",
  item,
  leftArea,
  showToggle = false,
  dataProcessor,
  clientPageSize,
  clientPaginateByRootLevelZero,
  externalSearchTerm,
  externalCurrentPage,
  onSearchChange,
  onPageChange,
  externalViewMode,
  onViewModeChange,
  gridClassName,
  embedded = false,
}: DynamicListProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<any[]>([]);
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [internalViewMode, setInternalViewMode] = useState<"list" | "grid">(type);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");

  const currentPage = externalCurrentPage ?? internalCurrentPage;
  const searchTerm = externalSearchTerm ?? internalSearchTerm;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      if (onPageChange) {
        onPageChange(page);
      } else {
        setInternalCurrentPage(page);
      }
    }
  };

  const handleSearch = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchTerm(value);
      setInternalCurrentPage(1);
    }
  };

  useEffect(() => {
    if (externalViewMode === undefined) {
      setInternalViewMode(type);
    }
  }, [type, externalViewMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      let response: BaseResponse | any;
      if (fetcher) {
        response = await fetcher(clientPageSize ? 1 : currentPage, searchTerm);
      } else {
        response = await ApiService.getApi(path, clientPageSize ? 1 : currentPage, searchTerm);
      }

      setData(response.data || []);
      if (!clientPageSize) {
        const meta = response.meta || {
          page: 1,
          totalPages: 1,
          totalItems: 0,
          limit: 20,
        };
        setTotalPages(meta.totalPages || 1);
        setTotalItems(meta.totalItems || 0);
        setLimit(meta.limit || 20);
      }
    } catch (error: any) {
      console.error("Failed to fetch:", error);
      setError(error.message || errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getClientPaginatedData = () => {
    if (!clientPageSize) return processedData;
    if (!clientPaginateByRootLevelZero) {
      const start = (currentPage - 1) * clientPageSize;
      return processedData.slice(start, start + clientPageSize);
    }
    const rootIndices: number[] = [];
    processedData.forEach((it, idx) => {
      const level = (it as any)?.level;
      if (typeof level === "number" && level === 0) rootIndices.push(idx);
    });
    const startRoot = (currentPage - 1) * clientPageSize;
    const endRoot = startRoot + clientPageSize;
    const startIndex = rootIndices[startRoot] ?? processedData.length;
    const endIndex = rootIndices[endRoot] ?? processedData.length;
    return processedData.slice(startIndex, endIndex);
  };

  const currentViewMode = externalViewMode ?? internalViewMode;

  const renderList = () => {
    return (
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          willChange: "scroll-position",
          transform: "translateZ(0)",
          contain: "layout style paint",
        }}>
        <div className={embedded ? "flex flex-col gap-2 sm:gap-3 pb-6" : "flex flex-col gap-2 sm:gap-3 pb-8"}>{getClientPaginatedData().map((row) => item(row, "list"))}</div>
      </div>
    );
  };

  const renderGrid = () => {
    return (
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          willChange: "scroll-position",
          transform: "translateZ(0)",
          contain: "layout style paint",
        }}>
        <div className="cq-grid-container">
          <div className={gridClassName || "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pb-8 items-stretch auto-rows-fr"}>
            {getClientPaginatedData().map((row, index) => (
              <div key={(row && (row.id || row.contentId || row.gainId)) ?? index} className="h-full">
                {item(row, "grid")}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getClassName = () => {
    if (currentViewMode === "list") return "flex flex-1 min-h-0 w-full gap-2";
    return "flex flex-col flex-1 min-h-0 w-full gap-2";
  };

  useEffect(() => {
    fetchData();
  }, [path, currentPage, searchTerm, refreshKey]);

  useEffect(() => {
    if (dataProcessor && data.length > 0) {
      const processed = dataProcessor(data, searchTerm);
      setProcessedData(processed);
      if (clientPageSize) {
        const totalRoots = clientPaginateByRootLevelZero
          ? processed.filter((x: any) => (typeof x?.level === "number" ? x.level === 0 : false)).length
          : processed.length;
        setTotalItems(totalRoots);
        setLimit(clientPageSize);
        setTotalPages(Math.max(1, Math.ceil(totalRoots / clientPageSize)));
      }
    } else {
      setProcessedData(data);
      if (clientPageSize) {
        const totalRoots = clientPaginateByRootLevelZero
          ? data.filter((x: any) => (typeof x?.level === "number" ? x.level === 0 : false)).length
          : data.length;
        setTotalItems(totalRoots);
        setLimit(clientPageSize);
        setTotalPages(Math.max(1, Math.ceil(totalRoots / clientPageSize)));
      }
    }
  }, [data, dataProcessor, searchTerm, clientPageSize, clientPaginateByRootLevelZero]);

  const paginationBar =
    isPagination ? (
      <div className="inline-flex max-w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-600 sm:px-2.5 sm:text-sm">
        <span>
          {totalItems > 0
            ? `${(currentPage - 1) * limit + 1}-${Math.min(currentPage * limit, totalItems)} / ${totalItems}`
            : "0 / 0"}
        </span>
        <button
          type="button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="rounded p-1 text-gray-500 transition hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="rounded p-1 text-gray-500 transition hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    ) : null;

  const showToolbarRow =
    (!header && !!actions) || isSearch || (isPagination && !header) || showToggle;

  return (
      <div
      className={
        embedded
          ? "relative flex min-h-0 w-full flex-1 flex-col"
          : "relative mr-0 flex h-full min-h-0 w-full flex-col bg-gray-50 sm:mr-1 sm:rounded-lg sm:shadow-sm"
      }>
      {header && (
        <header className="border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-xl font-semibold text-gray-900">{title}</span>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              {actions && (
                <div className="w-full sm:w-auto">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 sm:justify-end">{actions}</div>
                </div>
              )}
              {isPagination && paginationBar}
            </div>
          </div>
        </header>
      )}

      <div
        className={
          embedded
            ? "flex min-h-0 w-full flex-1 flex-col"
            : "flex min-h-0 w-full flex-1 flex-col px-3 py-4 sm:px-6 sm:py-6"
        }>
        {error && (
          <div
            className={`rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 ${
              embedded ? "mx-3 mt-3 shrink-0 sm:mx-6" : "mb-4"
            }`}>
            <p>{error}</p>
          </div>
        )}

        {showToolbarRow && (
        <div
          className={
            embedded ? "shrink-0 border-b border-gray-200 px-3 py-3 sm:px-6" : "mb-4 py-1"
          }>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
            {!header && actions && (
              <div className="flex min-w-0 flex-[0_1_auto] flex-wrap items-center gap-2">{actions}</div>
            )}
            {isSearch && (
              <div className="relative min-h-[42px] min-w-0 flex-[1_1_16rem]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="search"
                  placeholder="Ara"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`w-full min-w-0 rounded-lg border border-gray-300 bg-white pl-10 pr-4 text-sm text-gray-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${
                    embedded ? "h-10 py-0 leading-none" : "py-2.5"
                  }`}
                />
              </div>
            )}
            {(isPagination && !header) || showToggle ? (
              <div className="ml-auto flex min-w-0 flex-shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
                {isPagination && !header && paginationBar}
                {showToggle && (
                  <div className="inline-flex shrink-0 items-center rounded-lg border border-gray-300 bg-white p-1">
                    <button
                      type="button"
                      onClick={() => (onViewModeChange ? onViewModeChange("grid") : setInternalViewMode("grid"))}
                      className={`rounded-md p-2 transition-colors ${currentViewMode === "grid" ? "bg-blue-100 text-blue-600" : "text-gray-600 hover:bg-gray-100"
                        }`}>
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => (onViewModeChange ? onViewModeChange("list") : setInternalViewMode("list"))}
                      className={`rounded-md p-2 transition-colors ${currentViewMode === "list" ? "bg-blue-100 text-blue-600" : "text-gray-600 hover:bg-gray-100"
                        }`}>
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
        )}

        <div
          className={`dynamic-list-shell dynamic-list-body flex min-h-0 w-full flex-1 gap-4 ${
            embedded ? "px-3 py-4 sm:px-6 sm:py-6" : ""
          }`}>
          {leftArea && (
            <div
              className="dynamic-list-left flex-shrink-0 overflow-y-auto mr-2 transition-all duration-300"
              style={{
                willChange: "scroll-position",
                transform: "translateZ(0)",
                contain: "layout style paint",
              }}>
              {leftArea()}
            </div>
          )}
          <div className="dynamic-list-main flex-1 min-h-0 flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center text-gray-600">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <span className="text-sm">Yükleniyor...</span>
                </div>
              </div>
            ) : processedData.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">{emptyMessage}</p>
              </div>
            ) : currentViewMode === "list" ? (
              renderList()
            ) : (
              renderGrid()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}